import { Component, Inject, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { add } from 'date-fns';

import { labelMaxLength, labelMinLength } from '../../../../app.constants';
import { CoreApiService } from '../../../../core-api.service';
import { LoggingService } from '../../../../logging.service';
import { NotificationService } from '../../../../notification.service';
import { ApiKeyResult, LegacyApiKey } from '../../../../shared/models/api-key.model';
import { generateElementId } from '../../../../shared/string.utils';

interface DialogData {
  context: 'device' | 'profile';
  deviceId?: string;
}
@Component({
  selector: 'generate-api-key-dialog',
  styleUrls: ['./generate-api-key-dialog.component.scss'],
  templateUrl: './generate-api-key-dialog.component.html',
})
export class GenerateApiKeyDialog {
  elementId = generateElementId();
  form = new FormGroup({
    keyName: new FormControl('', [Validators.required, Validators.maxLength(labelMaxLength), Validators.minLength(labelMinLength)]),
    expirationDate: new FormControl(add(new Date(), { months: 1 }), [Validators.required]),
  });
  displayUnencryptedSecret = '';
  displayCliCommand = '';
  generatedKey: LegacyApiKey;
  showUnencryptedSecret = false;
  showCliCommand = false;
  state: 'generateKey' | 'displayKey' = 'generateKey';
  secretCopied = false;
  CLICopied = false;
  minDate = new Date(); //Sets the date to the current day for the min validation of the datepicker

  constructor(
    @Inject(MAT_DIALOG_DATA) @Optional() private data: DialogData,
    private dialogRef: MatDialogRef<GenerateApiKeyDialog>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService
  ) {}

  submit(): void {
    const endOfDay = new Date(this.form.controls.expirationDate.value);
    endOfDay.setHours(23, 59, 59);
    const params = {
      label: this.form.controls.keyName.value,
      expires_at: endOfDay.toISOString(),
    }
    if (this.data.context === 'profile') {
      this.coreApi.users.apiKeys.generateKey(params).subscribe({
        next: (resp) => {
          this.generateKeySuccess(resp);
        },
        error: (error) => {
          this.generateKeyError(error);
        },
      });
    } else if (this.data.context === 'device') {
      this.coreApi.devices.generateKey(this.data.deviceId, params).subscribe({
        next: (resp) => {
          this.generateKeySuccess(resp);
        },
        error: (error) => {
          this.generateKeyError(error);
        },
      });
    }
  }

  generateKeySuccess(resp: ApiKeyResult): void {
    // Upon success, change state and display new api key to user and don't close modal
    this.state = 'displayKey';
    this.generatedKey = Object.assign({}, {
      created: resp.created,
      key: resp.key,
      label: this.form.controls.keyName.value,
      last_used: resp.last_used,
    });
    this.displayUnencryptedSecret = `${window.location.host}:${resp.unencrypted_secret}`;
    this.displayCliCommand = `fw login ${window.location.host}:${resp.unencrypted_secret}`;
  }

  generateKeyError(error): void {
    this.logger.error(error);
    if (error.status === 422) {
      this.notifications.error('generateApiKey.submit.duplicate');
    } else {
      this.notifications.error('generateApiKey.submit.error');
    }
  }

  close(): void {
    this.dialogRef.close(this.generatedKey);
  }

  getLabelId(tag: string): string {
    return `${this.elementId}-${tag}`;
  }

  copyToClipboard(context): void {
    if (context === 'secret') {
      navigator.clipboard.writeText(this.displayUnencryptedSecret).then(() => { this.secretCopied = true });
    } else {
      navigator.clipboard.writeText(this.displayCliCommand).then(() => { this.CLICopied = true });
    }
  }
}
